export const getPercentValue = (value, max) => {
  // Prevent NaN and Infinity values from being returned
  if (max === 0 || isNaN(value / max)) {
    return 0
  }
  return (value / max) * 100
}

export const getHostUsageTotals = (hosts = []) => {
  const usageTotals = hosts.reduce(
    (accum, host) => {
      return {
        compute: {
          current: accum.compute.current + (host.usage?.compute?.current || 0),
          max: accum.compute.max + (host.usage?.compute?.max || 0),
          units: 'GHz',
        },
        blockStorageDisk: {
          current: accum.blockStorageDisk.current + (host.usage?.disk?.current || 0),
          max: accum.blockStorageDisk.max + (host.usage?.disk?.max || 0),
          units: 'GiB',
        },
        ephemeralDisk: {
          current: accum.ephemeralDisk.current + (host.usage?.ephemeralDisk?.current || 0),
          max: accum.ephemeralDisk.max + (host.usage?.ephemeralDisk?.max || 0),
          units: 'GiB',
        },
        memory: {
          current: accum.memory.current + (host.usage?.memory?.current || 0),
          max: accum.memory.max + (host.usage?.memory?.max || 0),
          units: 'GiB',
        },
      }
    },
    {
      compute: {
        current: 0,
        max: 0,
        units: 'GHz',
      },
      blockStorageDisk: {
        current: 0,
        max: 0,
        units: 'GiB',
      },
      ephemeralDisk: {
        current: 0,
        max: 0,
        units: 'GiB',
      },
      memory: {
        current: 0,
        max: 0,
        units: 'GiB',
      },
    },
  )

  return {
    compute: {
      ...usageTotals.compute,
      percent: getPercentValue(usageTotals.compute.current, usageTotals.compute.max),
    },
    blockStorageDisk: {
      ...usageTotals.blockStorageDisk,
      percent: getPercentValue(
        usageTotals.blockStorageDisk.current,
        usageTotals.blockStorageDisk.max,
      ),
    },
    ephemeralDisk: {
      ...usageTotals.ephemeralDisk,
      percent: getPercentValue(usageTotals.ephemeralDisk.current, usageTotals.ephemeralDisk.max),
    },
    memory: {
      ...usageTotals.memory,
      percent: getPercentValue(usageTotals.memory.current, usageTotals.memory.max),
    },
  }
}

export const getHostAllocationTotals = ({ hosts = [], hypervisors = [], storagePools = [] }) => {
  const hostIds = hosts.map((host) => host?.id)
  const hostHypervisors = hypervisors.filter((hypervisor) =>
    hostIds?.some((id) => hypervisor?.service?.host === id),
  )
  const hypervisorAllocation = hostHypervisors.reduce(
    (accum, hypervisor) => {
      return {
        compute: {
          current: accum.compute.current + hypervisor.vcpus_used,
          max: accum.compute.max + hypervisor.vcpus,
          units: 'vCPUs',
        },
        memory: {
          current: accum.memory.current + hypervisor.memory_mb_used / 1024,
          max: accum.memory.max + hypervisor.memory_mb / 1024,
          units: 'GiB',
        },
        ephemeralStorage: {
          current: accum.ephemeralStorage.current + hypervisor.local_gb_used,
          max: accum.ephemeralStorage.max + hypervisor.local_gb,
          units: 'GB',
        },
      }
    },
    {
      compute: {
        current: 0,
        max: 0,
      },
      memory: {
        current: 0,
        max: 0,
      },
      ephemeralStorage: {
        current: 0,
        max: 0,
      },
    },
  )
  const hypervisorAllocationWithPercent = {
    compute: {
      ...hypervisorAllocation.compute,
      percent: getPercentValue(
        hypervisorAllocation.compute.current,
        hypervisorAllocation.compute.max,
      ),
    },
    memory: {
      ...hypervisorAllocation.memory,
      percent: getPercentValue(
        hypervisorAllocation.memory.current,
        hypervisorAllocation.memory.max,
      ),
    },
    ephemeralStorage: {
      ...hypervisorAllocation.ephemeralStorage,
      percent: getPercentValue(
        hypervisorAllocation.ephemeralStorage.current,
        hypervisorAllocation.ephemeralStorage.max,
      ),
    },
  }
  const hostStoragePools = storagePools.filter((storagePool) =>
    hostIds?.some((id) => storagePool?.name?.includes(id)),
  )
  const hostStorageAllocation = hostStoragePools.reduce(
    (accum, storagePool) => {
      return {
        blockStorage: {
          current: accum.blockStorage.current + storagePool?.capabilities?.allocated_capacity_gb,
          max: accum.blockStorage.max + storagePool?.capabilities?.total_capacity_gb,
          units: 'GB',
        },
      }
    },
    {
      blockStorage: {
        current: 0,
        max: 0,
      },
    },
  )
  const hostStorageAllocationWithPercent = {
    blockStorage: {
      ...hostStorageAllocation.blockStorage,
      percent: getPercentValue(
        hostStorageAllocation.blockStorage.current,
        hostStorageAllocation.blockStorage.max,
      ),
    },
  }
  return {
    ...hypervisorAllocationWithPercent,
    ...hostStorageAllocationWithPercent,
  }
}

export const CPU_ALLOCATION_RATIO_TOOLTIP =
  'Specifies the number of vCPUs to allocate per physical core. For example, an allocation ratio of 1 implies no overcommitment, while an allocation ratio of 2 indicates two vCPUs allocated per physical core.'
export const MEMORY_ALLOCATION_RATIO_TOOLTIP =
  'Specifies how much RAM to allocate relative to the host’s physical RAM. For example, an allocation ratio of 1 implies no overcommitment, while an allocation ratio of 2 indicates allocating twice the amount of the host’s physical RAM.'
export const EPHEMERAL_DISK_ALLOCATION_RATIO_TOOLTIP =
  'Specifies the amount of ephemeral disk space to allocate per gigabyte of physical disk storage. For example, an ephemeral disk allocation ratio of 1 implies no overcommitment, while an allocation ratio of 2 indicates allocating twice the amount of the host’s physical disk storage.'
